import { useMutation, useQueryClient } from '@tanstack/react-query';
import { USER_ROLE } from 'khufu-legos';
import React, { useState } from 'react';
import { Button, CloseButton, Form, Modal, Toast } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { cancelOrder, cancelOrderWithReturns } from '../../../apis/queries';
import { RootState } from '../../../store/redux/store';
import { Order } from '../../../types';
import { ORDERS_QUERY_KEY } from '../Orders';

interface CancelOrderModalProps {
  show: boolean;
  onHide: () => void;
  order: Order | null;
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({ show, onHide, order }) => {
  const [reason, setReason] = useState('');
  const [showToast, setShowToast] = useState(false);
  const queryClient = useQueryClient();
  const userDetails = useSelector((state: RootState) => state.user.currentUser);
  const isAdmin = userDetails?.role === USER_ROLE.ADMIN;

  const mutation = useMutation({
    mutationFn: (data: { orderId: string; reason: string; hasReturn?: boolean }) =>
      isAdmin && data.hasReturn
        ? cancelOrderWithReturns(data.orderId, data.reason)
        : cancelOrder(data.orderId, data.reason),
    onSuccess: () => {
      setShowToast(true);
      onHide();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDERS_QUERY_KEY]
      });
    }
  });

  const handleCancel = () => {
    if (order) {
      const hasReturn = order.hasReturnOrder;
      mutation.mutate({ orderId: order._id.toString(), reason, hasReturn });
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header>
          <h3 className="card-header-title">
            Cancel Order <span className="fw-bold">{order?.orderNumber}</span>{' '}
          </h3>
          <CloseButton onClick={onHide} />
        </Modal.Header>
        <Modal.Body className="overflow-auto d-flex flex-column flex-grow-1">
          <p>This action is not reversible.</p>
          <p>Confirming this will cancel the order. Stock and empties will be reversed.</p>

          <Form.Group className="mb-3">
            {/* <Form.Label>Reason for Cancelling Order</Form.Label> */}
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              required
              onChange={e => setReason(e.target.value)}
              placeholder="Enter the reason for cancelling..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Back
          </Button>
          <Button
            style={{ backgroundColor: 'red', borderColor: 'red' }}
            onClick={handleCancel}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Cancelling...' : 'Cancel'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 9999,
          backgroundColor: 'green',
          color: 'white'
        }}
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
      >
        <Toast.Body>Order was successfully canceled!</Toast.Body>
      </Toast>
    </>
  );
};

export default CancelOrderModal;
